// Patient education material — auto-suggested by Known Case Of chips plus
// the drug list. Each entry has a short title (for the checklist), a
// one-paragraph body (printed on the Rx if the doctor ticks it), and a
// `matches(ctx)` predicate. Doctor picks which handouts to include on print.

const includesAny = (haystack, needles) => {
  const s = (haystack || "").toLowerCase();
  return needles.some((n) => s.includes(n.toLowerCase()));
};
const rxContains = (medicines, term) => (medicines || []).some((m) => `${m.generic_name || ""} ${m.composition || ""} ${m.brand_name || ""}`.toLowerCase().includes(term.toLowerCase()));
const knownAny = (known, terms) => (known || []).some((k) => terms.some((t) => k.toLowerCase().includes(t.toLowerCase())));
// Regex-based match so we can use word boundaries (\bdm\b) and avoid
// false-positives like "hospital admission" hitting `dm`.
const knownAnyRx = (known, regexes) => (known || []).some((k) => regexes.some((rx) => rx.test(k)));
const DM_RX = [/\bdm\b/i, /\bt1dm\b/i, /\bt2dm\b/i, /diabet/i];

// Marathi translations for each handout. Keyed by handout `id`. Written in
// clear, spoken Marathi (Devanagari) that patients and their families in
// Maharashtra will understand. Kept in a separate map so the English library
// stays untouched.
export const EDUCATION_MR = {
  "dm-foot-care": {
    title: "मधुमेहात पायांची काळजी",
    body: "दररोज पायांची तपासणी करा — कोणतीही जखम, फोड, लालसरपणा किंवा सूज नाही ना पाहा. कोमट पाण्याने पाय स्वच्छ धुऊन बोटांच्या मधून पूर्ण कोरडे करा. मऊ, बंद चप्पल किंवा शूज वापरा — कधीही अनवाणी चालू नका. नखे सरळ कापा. एखादी जखम ३ दिवसांत भरत नसेल तर लगेच डॉक्टरांना दाखवा.",
  },
  "dm-hypoglycaemia": {
    title: "साखर कमी होणे (हायपोग्लायसीमिया) ओळखणे व उपचार",
    body: "लक्षणे: थरथर, घाम, अचानक भूक, धडधड, गोंधळ. लगेच १५ ग्रॅम गोड घ्या — पाण्यात ३ चमचे साखर, १०० मिली फळांचा रस, किंवा ३ ग्लुकोज गोळ्या. १५ मिनिटांनी पुन्हा साखर तपासा; ७० mg/dL पेक्षा कमी असेल तर पुन्हा घ्या. नंतर छोटा आहार घ्या. साखरेच्या गोळ्या नेहमी सोबत ठेवा.",
  },
  "dm-diet": {
    title: "मधुमेहासाठी योग्य आहार",
    body: "ताटातील अर्धा भाग भाज्या, चतुर्थांश धान्य (नाचणी/तांदूळ/भाकरी), चतुर्थांश प्रथिने (डाळ, अंडे, कोंबडी, मासे). गोड पेये, मिठाई व पॅकबंद रस टाळा. ठराविक वेळेस जेवण घ्या. मीठ न लावलेले सुके मेवे (एक मूठभर) खाऊ शकता.",
  },
  "dm-exercise": {
    title: "व्यायाम व शारीरिक हालचाल",
    body: "आठवड्यातून १५० मिनिटे मध्यम व्यायाम — ३० मिनिटे भरभर चालणे, आठवड्यात ५ दिवस. आठवड्यातून दोनदा हलका वजन उचलण्याचा व्यायाम करा. इन्सुलिन/SU घेत असाल तर व्यायामाच्या आधी व नंतर साखर तपासा.",
  },
  "dm-sick-day": {
    title: "आजारपणाच्या दिवसांचे नियम",
    body: "आजार असताना: इन्सुलिन कधीच बंद करू नका, भरपूर पाणी प्या, दर ४ तासांनी साखर तपासा; साखर २५० mg/dL च्या वर असेल तर मूत्रात कीटोन तपासा. उलट्या, कीटोन जास्त किंवा गुंगीसारखी अवस्था असेल तर लगेच दवाखान्यात संपर्क साधा.",
  },
  "sglt2-sick-day": {
    title: "SGLT2 औषध — आजारपणाचे नियम",
    body: "कोणत्याही तीव्र आजारात — उलट्या, जुलाब, ताप, किंवा नीट जेवता येत नसेल — SGLT2 (एम्पा/डापा) थांबवा; डीकेएचा धोका असतो. पूर्ण बरे झाल्यावर २४ तासांनी पुन्हा सुरू करा. गुप्तांग नियमित स्वच्छ धुवा; जळजळ किंवा खाज असेल तर सांगा.",
  },
  "glp1-nausea": {
    title: "GLP-1 इंजेक्शन — मळमळ हाताळणे",
    body: "पहिल्या २-४ आठवड्यांत हलकी मळमळ होणे सामान्य आहे व नंतर आपोआप कमी होते. कमी-कमी वेळा खा, हळू चावा, तेलकट-मसालेदार टाळा. पोट/मांडी/दंडाचा वरचा भाग यांच्यात इंजेक्शनची जागा बदलत रहा. पाठीपर्यंत जाणारे तीव्र पोटदुखी असेल तर लगेच सांगा.",
  },
  "statin-myopathy": {
    title: "स्टॅटिन — स्नायूदुखीबद्दल सतर्कता",
    body: "मांडी किंवा खांद्यात नवीन दुखणे, दुखरेपण किंवा कमजोरी, किंवा गडद रंगाची लघवी असेल तर लगेच सांगा. बहुतेक स्नायूदुखी सौम्य असते व औषध थांबवण्याची गरज नसते. अटोर्वा/सिम्वा स्टॅटिनसोबत मोसंबी (grapefruit) खाऊ नये.",
  },
  "ace-cough": {
    title: "ACE ब्लॉकर — कोरडा खोकला",
    body: "ACE औषधांनी (रामिप्रिल, एनालाप्रिल) १०% रुग्णांना कोरडा खोकला होतो. हा धोकादायक नसतो पण त्रासदायक असतो. आम्हाला सांगा; आपण ARB मध्ये बदलू शकतो जे तेच काम कोरडा खोकल्याशिवाय करते.",
  },
  "aspirin-bleed": {
    title: "एस्प्रिन — रक्तस्रावाची लक्षणे",
    body: "एस्प्रिन रक्त पातळ करते. काळे/गडद शौच, लघवीत रक्त, विनाकारण अगदी सहज होणारा घाव, किंवा दीर्घकाळ थांबत नसलेला नाकातून रक्तस्राव यांची तक्रार लगेच सांगा. जेवणासोबत घ्या. शस्त्रक्रिया किंवा दंतोपचारापूर्वी एस्प्रिन घेत असल्याचे नक्की सांगा.",
  },
  "warfarin-inr": {
    title: "वारफेरिन व INR तपासणी",
    body: "ठरलेल्या वेळेस INR तपासा. आहार स्थिर ठेवा — पालेभाज्या (पालक, कोबी, ब्रोकोली) अचानक जास्त खाल्ल्यास वारफेरिनचा प्रभाव कमी होतो. दारू टाळा. प्रत्येक डॉक्टर/दंतवैद्याला वारफेरिन घेत आहे हे सांगा.",
  },
  "doac-bleed": {
    title: "DOAC (अपिक्साबान/रिवारोक्साबान) — रक्तस्राव सतर्कता",
    body: "दररोज त्याच वेळेस घ्या. एकही डोस चुकवू नका. रिवारोक्साबान जेवणासोबत घ्या. रक्तस्राव, काळे शौच, अचानक तीव्र डोकेदुखी, किंवा पडणे/लागणे झाले तर लगेच येऊन दाखवा. शस्त्रक्रिया/दंतोपचारापूर्वी हे औषध सांगा.",
  },
  "levothyroxine-timing": {
    title: "लेवोथायरॉक्सिन — योग्य वेळी घेणे",
    body: "लेवोथायरॉक्सिन उपाशीपोटी सकाळी पहिल्यांदा घ्या — नाश्त्यापूर्वी किमान ३०-६० मिनिटे. चहा, कॉफी, कॅल्शियम, लोह किंवा अँटासिडसोबत घेऊ नका — शोषण कमी होते. दररोज त्याच वेळेस. ६-८ आठवड्यांनी TSH तपासा.",
  },
  "metformin-b12": {
    title: "मेटफोर्मिन — दीर्घकाळ वापर व B12",
    body: "मेटफोर्मिन दीर्घकाळ घेतल्यास व्हिटॅमिन B12 कमी होऊ शकते (थकवा, हातपायांत बधीरपणा किंवा मुंग्या). वर्षातून एकदा B12 तपासून घ्या. पोटावर परिणाम कमी होण्यासाठी मेटफोर्मिन जेवणासोबत घ्या.",
  },
  "ppi-deprescribe": {
    title: "PPI — नियमित पुनरावलोकन",
    body: "पँटो/ओमेप्रा दीर्घकाळ घेतल्यास मॅग्नेशियम व B12 कमी होऊ शकते व हाडे कमजोर होण्याचा थोडा धोका असतो. लक्षणे नियंत्रणात आली की ८ आठवड्यांनंतर डोस कमी करावा किंवा थांबवावा. आयुष्यभर स्वतःच सुरू ठेवू नका.",
  },
  "dkd-monitoring": {
    title: "मधुमेहामुळे मूत्रपिंडाचा आजार (DKD) — खबरदारी",
    body: "BP १३०/८० पेक्षा कमी व HbA1c लक्ष्यानुसार ठेवा. दिवसाला ५ ग्रॅमपेक्षा कमी मीठ (एक सपाट चमचा). पेनकिलर (आयबुप्रोफेन/डायक्लोफेनाक) टाळा — मूत्रपिंडाला अपाय होतो. पाय सुजणे किंवा लघवी कमी होणे लगेच सांगा. दर ३-६ महिन्यांनी क्रिएटिनिन, eGFR व UACR तपासून घ्या.",
  },
  "hfref-daily-weight": {
    title: "हृदयक्रिया कमजोर — दररोजचे वजन",
    body: "दररोज सकाळी शौचानंतर, नाश्त्यापूर्वी, त्याच कपड्यांत वजन घ्या व नोंदवा. ३ दिवसांत २ किलो किंवा एका आठवड्यात ३ किलो वाढ झाली तर लगेच फोन करा — शरीरात पाणी साठले असावे, औषधात बदल लागेल. मीठ ५ ग्रॅम/दिवसापेक्षा कमी. द्रवपदार्थ ~१.५-२ लीटर/दिवस.",
  },
  "htn-lifestyle": {
    title: "उच्च रक्तदाब — जीवनशैली",
    body: "मीठ ५ ग्रॅम/दिवसापेक्षा कमी (लोणचे, बिस्कीट, नमकीन यांतील लपलेले मीठ धरून). दारू कमी करा. आठवड्यात ५ दिवस ३० मिनिटे भरभर चालणे. आठवड्यातून दोनदा घरी BP तपासा — लक्ष्य १३०/८० पेक्षा कमी.",
  },
  "dyslipidaemia": {
    title: "कोलेस्टेरॉल — आहारातील टिप्स",
    body: "तळलेले, मिठाई व पॅकबंद स्नॅक्स कमी करा. सुका मेवा, तेलकट मासे (आठवड्यात २ वेळा), पूर्ण धान्य व डाळी खा. मोहरी, शेंगदाणा, ऑलिव्ह ऑइल वापरा. दररोज ३०-४५ मिनिटे भरभर चालणे.",
  },
  "cad-postmi": {
    title: "हार्ट अटॅकनंतर — सुरक्षित जीवन",
    body: "प्रत्येक हृदयाचे औषध दररोज घ्या — कधीच स्वतःहून थांबवू नका. कार्डिअक रिहॅबला जा. छातीत घट्टपणा, जबडा/हातातील दुखणे, धाप लागणे किंवा चक्कर लगेच सांगा. धूम्रपान पूर्ण बंद करा. BP १३०/८० पेक्षा कमी व LDL ५५ mg/dL पेक्षा कमी ठेवा.",
  },
  "vit-d": {
    title: "व्हिटॅमिन D — सूर्यप्रकाश व आहार",
    body: "सकाळचा १५-२० मिनिटे सूर्यप्रकाश (हातपाय व चेहरा उघडा) आठवड्यात ४-५ दिवस. अंडी, तेलकट मासे व फोर्टिफाईड दूध आहारात ठेवा. डॉक्टरांनी दिलेला डोस चुकवू नका. ८-१२ आठवड्यांनी व्हिटॅमिन D पुन्हा तपासा.",
  },
  "smoking-cessation": {
    title: "धूम्रपान सोडणे — कशी सुरुवात करावी",
    body: "२ आठवड्यांच्या आत सोडण्याची निश्चित तारीख ठरवा. घरातील राखदाणी व सिगारेट काढून टाका. निकोटिन गम/पॅच बाजारात उपलब्ध — तल्लफ कमी होते. पहिल्या ३-५ दिवसांत त्रास सर्वात जास्त होतो व नंतर कमी होतो. गरज असल्यास औषध (बुप्रोपायॉन/व्हॅरेनिक्लिन) बद्दल विचारा.",
  },
  "plate-method": {
    title: "मधुमेह — प्लेट पद्धत",
    body: "ताटाचे 3 भाग: १/२ भाग स्टार्च नसलेल्या भाज्या (पालक, कोबी, गवार, वांगे), १/४ भाग प्रथिने (डाळ, अंडे, चिकन/मासे), १/४ भाग कर्बयुक्त पदार्थ (भाकरी/तांदूळ/चपाती — मूठभर). एक ग्लास ताक/दही. गोड पदार्थ व साखर-असलेली पेये टाळा. ही सोपी पद्धत रोज पाळल्यास ठराविक कर्बकण मिळतात व साखर स्थिर राहते.",
  },
  "carb-counting": {
    title: "कर्बकण मोजणे — कार्ब काउंटिंग",
    body: "एक कार्ब चॉईस = १५ ग्रॅम कर्बकण. उदा: १ मध्यम चपाती/भाकरी, १/३ वाटी तांदूळ, १ मध्यम फळ (सफरचंद/केळे अर्धे), १ ग्लास दूध. जेवणात २-३ कार्ब चॉईस (३०-४५ ग्रॅम) घ्या. उच्च-कर्ब पदार्थ (साखर, रस, बिस्किट) कमीत कमी. साखरेच्या चढ-उतारावर हुशारीने नियंत्रण मिळते.",
  },
  "nutrition-labels": {
    title: "पॅकेटवरील पोषक-लेबल कसे वाचावे",
    body: "प्रथम 'Serving size' पाहा — डोसाच्या आकाराचे प्रमाण. मग 'Total Carbohydrate' (एकूण कर्बकण) पाहा — त्यातून 'Dietary Fibre' वजा केल्यास 'Net Carbs' मिळतात. एका जेवणात <३० ग्रॅम नेट कार्ब ठेवा. 'Added Sugars' शून्य असल्यास सर्वोत्तम. Sodium <5% DV असल्यास कमी मीठ.",
  },
};

// A missing translation is NOT English content labelled as another language.
export function getEducationText(id, lang) {
  const en = EDUCATION_LIBRARY.find((e) => e.id === id);
  if (!en) return null;
  if (lang === "mr" && EDUCATION_MR[id]) {
    return { id, title: EDUCATION_MR[id].title, body: EDUCATION_MR[id].body };
  }
  return lang === "en" ? { id, title: en.title, body: en.body } : null;
}

export const EDUCATION_LIBRARY = [
  // ===== Diabetes core =====
  {
    id: "dm-foot-care",
    title: "Diabetic foot care",
    body: "Inspect feet daily for cuts, blisters, redness or swelling. Wash with lukewarm water and dry between toes. Wear soft, closed footwear — never walk barefoot. Trim nails straight across. Report any wound that has not started healing within 3 days.",
    matches: ({ known }) => knownAnyRx(known, DM_RX),
  },
  {
    id: "dm-hypoglycaemia",
    title: "Recognising & treating hypoglycaemia (low sugar)",
    body: "Symptoms: shakiness, sweating, hunger, palpitations, confusion. Treat immediately with 15 g fast sugar (3 teaspoons sugar in water, or 100 ml fruit juice, or 3 glucose tablets). Recheck sugar after 15 minutes — repeat if still < 70 mg/dL. Follow with a small snack. Carry sugar on your person at all times.",
    matches: ({ medicines, known }) =>
      rxContains(medicines, "insulin") || rxContains(medicines, "glimepiride") || rxContains(medicines, "gliclazide") || knownAnyRx(known, DM_RX),
  },
  {
    id: "dm-diet",
    title: "Diabetes-friendly eating pattern",
    body: "Half the plate should be vegetables, one-quarter whole grains (millets/brown rice/roti), one-quarter protein (dal, egg, chicken, fish). Avoid sugary drinks, sweets, and packaged juices. Eat at fixed times. Snack on unsalted nuts (a small handful).",
    matches: ({ known }) => knownAnyRx(known, [...DM_RX, /prediabet/i, /metabolic syndrome/i]),
  },
  {
    id: "dm-exercise",
    title: "Physical activity",
    body: "Aim for 150 minutes of moderate activity per week — a brisk 30-minute walk, five days. Add two sessions of resistance/strength work. Check sugar before and after activity if on insulin/SU.",
    matches: ({ known }) => knownAnyRx(known, [...DM_RX, /obes/i, /metabolic syndrome/i]),
  },
  {
    id: "dm-sick-day",
    title: "Sick-day rules",
    body: "When ill: continue insulin (never stop), drink plenty of water, check sugar every 4 hours, and check urine ketones if sugar > 250 mg/dL. Contact us urgently if vomiting, ketones ++ or drowsy.",
    matches: ({ medicines, known }) => rxContains(medicines, "insulin") || knownAny(known, ["type 1", "t1dm"]),
  },

  // ===== Drug-specific =====
  {
    id: "sglt2-sick-day",
    title: "SGLT2 inhibitor — sick-day rules",
    body: "Stop your SGLT2 (Empa/Dapa) during any acute illness with vomiting, diarrhoea, fever, or when you cannot eat — risk of dangerous DKA. Restart 24 hours after full recovery. Wash the genital area daily and report any burning or itching.",
    matches: ({ medicines }) => rxContains(medicines, "empagliflozin") || rxContains(medicines, "dapagliflozin") || rxContains(medicines, "canagliflozin") || rxContains(medicines, "sglt"),
  },
  {
    id: "glp1-nausea",
    title: "GLP-1 injection — managing nausea",
    body: "Mild nausea is common in the first 2-4 weeks and settles as your body adjusts. Eat smaller meals, chew slowly, avoid greasy/spicy food. Rotate injection sites (abdomen / thigh / upper arm). Report severe or persistent abdominal pain, especially radiating to back.",
    matches: ({ medicines }) => rxContains(medicines, "semaglutide") || rxContains(medicines, "liraglutide") || rxContains(medicines, "dulaglutide") || rxContains(medicines, "tirzepatide"),
  },
  {
    id: "statin-myopathy",
    title: "Statin — muscle pain reporting",
    body: "Report any new muscle pain, tenderness or weakness, especially in the thighs or shoulders, and any dark-coloured urine. Most muscle aches on statins are mild and do not require stopping — but rare rhabdomyolysis must be caught early. Do NOT eat grapefruit or drink grapefruit juice with atorvastatin/simvastatin.",
    matches: ({ medicines }) => rxContains(medicines, "atorvastatin") || rxContains(medicines, "rosuvastatin") || rxContains(medicines, "simvastatin") || rxContains(medicines, "pravastatin"),
  },
  {
    id: "ace-cough",
    title: "ACE-inhibitor — dry cough",
    body: "Around 1 in 10 patients develop a persistent dry cough on ACE inhibitors (Ramipril, Enalapril). This is not dangerous but is bothersome — please tell us and we will switch to an ARB, which does the same job without the cough.",
    matches: ({ medicines }) => rxContains(medicines, "ramipril") || rxContains(medicines, "enalapril") || rxContains(medicines, "lisinopril") || rxContains(medicines, "perindopril"),
  },
  {
    id: "aspirin-bleed",
    title: "Aspirin — bleeding awareness",
    body: "Aspirin thins the blood. Report any black tarry stools, blood in urine, unusually heavy bruising, or nosebleeds that don't stop. Take with food and always disclose aspirin use before surgery or dental procedures.",
    matches: ({ medicines }) => rxContains(medicines, "aspirin") || rxContains(medicines, "ecosprin"),
  },
  {
    id: "warfarin-inr",
    title: "Warfarin & INR monitoring",
    body: "Have INR checked as scheduled. Maintain a consistent diet — sudden increases in green leafy vegetables (spinach, cabbage, broccoli) reduce warfarin's effect. Avoid alcohol. Tell every doctor / dentist you are on warfarin.",
    matches: ({ medicines }) => rxContains(medicines, "warfarin") || rxContains(medicines, "acenocoumarol"),
  },
  {
    id: "doac-bleed",
    title: "DOAC (Apixaban / Rivaroxaban) — bleeding awareness",
    body: "Take at the same time each day. Never miss a dose. Rivaroxaban must be taken with food. Report bleeding, black stools, sudden severe headache, or fall/injury urgently. Disclose the medication before any surgery or dental procedure.",
    matches: ({ medicines }) => rxContains(medicines, "apixaban") || rxContains(medicines, "rivaroxaban") || rxContains(medicines, "dabigatran") || rxContains(medicines, "edoxaban"),
  },
  {
    id: "levothyroxine-timing",
    title: "Levothyroxine — how to take it",
    body: "Take levothyroxine on an empty stomach, first thing in the morning, at least 30-60 minutes before breakfast. Do NOT take with tea, coffee, calcium, iron, or antacids — they block absorption. Same time every day. Recheck TSH in 6-8 weeks.",
    matches: ({ medicines }) => rxContains(medicines, "levothyroxine") || rxContains(medicines, "thyronorm") || rxContains(medicines, "eltroxin"),
  },
  {
    id: "metformin-b12",
    title: "Metformin — long-term Vitamin B12",
    body: "Long-term metformin can lower Vitamin B12 levels (fatigue, numbness/tingling in hands or feet). Ask us to check B12 every 12 months. Take metformin with meals to reduce stomach upset.",
    matches: ({ medicines }) => rxContains(medicines, "metformin"),
  },
  {
    id: "ppi-deprescribe",
    title: "PPI — regular review",
    body: "Long-term acid blockers (Pantoprazole, Omeprazole) can lower magnesium and vitamin B12 and slightly raise fracture risk. If your symptoms are controlled, we should reduce or stop this medicine after 8 weeks. Do not self-continue lifelong.",
    matches: ({ medicines }) => rxContains(medicines, "pantoprazole") || rxContains(medicines, "omeprazole") || rxContains(medicines, "rabeprazole") || rxContains(medicines, "esomeprazole"),
  },

  // ===== Complication-specific =====
  {
    id: "dkd-monitoring",
    title: "Diabetic kidney disease (DKD) — protecting your kidneys",
    body: "Keep BP < 130/80 and HbA1c at your individual target. Salt intake < 5 g/day (one level teaspoon). Avoid painkillers like ibuprofen/diclofenac — they harm kidneys. Report puffy legs or reduced urine output. Get creatinine, eGFR and urine ACR done every 3-6 months.",
    matches: ({ known }) => knownAny(known, ["dkd", "diabetic kidney", "ckd", "nephropathy", "albuminuria", "proteinuria"]),
  },
  {
    id: "hfref-daily-weight",
    title: "Heart failure — daily weight log",
    body: "Weigh yourself every morning after using the toilet, before breakfast, in the same clothes. Write it down. Call us if you gain > 2 kg in 3 days or > 3 kg in a week — this usually means fluid is building up and your dose may need adjustment. Salt < 5 g/day. Limit fluid to ~1.5-2 L/day.",
    matches: ({ known }) => knownAny(known, ["heart failure", "hfref", "hfpef"]),
  },
  {
    id: "htn-lifestyle",
    title: "Hypertension — lifestyle",
    body: "Salt < 5 g/day (one level teaspoon including hidden salt in bread, pickles, snacks). Reduce alcohol. Regular walking 30 minutes × 5 days a week. Home BP monitoring twice a week — target < 130/80.",
    matches: ({ known }) => knownAny(known, ["hypertens", "htn"]),
  },
  {
    id: "dyslipidaemia",
    title: "Cholesterol — dietary tips",
    body: "Reduce fried foods, sweets and processed snacks. Prefer nuts, seeds, oily fish (2× a week), whole grains and pulses. Cook in oils high in mono/poly-unsaturates (mustard, groundnut, olive). Aim for 30-45 min brisk walking daily.",
    matches: ({ known }) => knownAny(known, ["dyslipid", "hyperlipid", "high cholesterol"]),
  },
  {
    id: "cad-postmi",
    title: "Post-MI / CAD — living safely",
    body: "Take every prescribed heart medicine daily — never stop on your own. Attend cardiac rehab. Report chest tightness, jaw/arm pain, breathlessness, or fainting immediately. Quit smoking absolutely. Keep BP < 130/80 and LDL < 55 mg/dL.",
    matches: ({ known }) => knownAny(known, ["cad", "post mi", "post-mi", "myocardial infarction", "post pci", "cabg", "ischaemic heart"]),
  },
  {
    id: "vit-d",
    title: "Vitamin D — sunlight & diet",
    body: "Get 15-20 minutes of morning sunlight (arms & face uncovered) 4-5 days a week. Include eggs, fatty fish and fortified milk in diet. Take supplement as prescribed. Recheck Vit D at 8-12 weeks.",
    matches: ({ medicines, known }) => rxContains(medicines, "cholecalciferol") || rxContains(medicines, "vitamin d") || knownAny(known, ["osteoporosis", "vitamin d defic"]),
  },
  {
    id: "smoking-cessation",
    title: "Quit smoking — how to start",
    body: "Set a firm quit date within 2 weeks. Tell family. Remove ashtrays and cigarettes from home. Nicotine gum/patch is available OTC — reduces cravings. Withdrawal peaks in the first 3-5 days and passes. Ask us about pharmacotherapy (bupropion / varenicline) if needed.",
    matches: ({ known }) => knownAny(known, ["smoker", "tobacco"]),
  },
  {
    id: "plate-method",
    title: "Diabetes Plate Method",
    body: "Fill half the plate with non-starchy vegetables (spinach, cabbage, gawar, brinjal), one-quarter with lean protein (dal, egg, chicken/fish), and one-quarter with carbohydrates (one small chapati/bhakri or half-cup rice). Add a glass of buttermilk or yoghurt. Skip sweetened drinks and sweets. This one habit alone keeps carb intake predictable and stabilises post-meal sugar.",
    matches: ({ known }) => knownAnyRx(known, [...DM_RX, /prediabet/i, /metabolic syndrome/i, /obes/i]),
  },
  {
    id: "carb-counting",
    title: "Carb identification & counting",
    body: "1 Carb Choice = 15 grams of carbohydrate. Examples: 1 medium chapati/bhakri, 1/3 cup cooked rice, 1 small fruit, 1 cup milk. Most meals should be 2-3 carb choices (30-45 g). Cut high-carb items (sweets, juice, biscuits) drastically. Counting carbs gives you real, minute-by-minute control over your sugar swings.",
    matches: ({ known, medicines }) => knownAnyRx(known, DM_RX) || rxContains(medicines, "insulin"),
  },
  {
    id: "nutrition-labels",
    title: "Reading nutrition labels",
    body: "Start with Serving size — every other number refers to that portion. Look at Total Carbohydrate, then subtract Dietary Fibre to get Net Carbs — target < 30 g per meal. Prefer 0 g Added Sugars. Sodium should be < 5 % of DV. If the label lists 'partially hydrogenated' oil (trans-fat) — put it back on the shelf.",
    matches: ({ known }) => knownAnyRx(known, [...DM_RX, /prediabet/i, /dyslipid/i, /obes/i, /hypertens/i]),
  },
];

// Return education entries that apply to the current visit. `ctx` = { known,
// medicines, allergyFlags }.
export function suggestEducation(ctx) {
  return EDUCATION_LIBRARY.filter((e) => {
    try { return e.matches(ctx); } catch { return false; }
  });
}
